import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { BaseParams, DetailsParams } from '@interfaces/app/nav.interfaces';
import { StockDetail } from '@interfaces/stock/stockDetail';
import { PivotRow } from '@interfaces/stock/stockOverview';
import { StockPackageDetail } from '@interfaces/stock/stockPackageDetail';
import { flattenResponses } from '@utils';
import { Observable, catchError, combineLatest, map, of, startWith, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

import { UnitPipe } from '@pipes/unit.pipe';
import { fixStockDetails, fixStockPackageDetail } from '@services/api.fixes';
import { totalWarehouseStock } from '@services/content.service';
import { NavService } from '@services/nav.service';

interface StockDetailsParams {
  sourceSystemId: string;
  stockType: string;
}

@Injectable({ providedIn: 'root' })
export class StockService {
  http = inject(HttpClient);
  navService = inject(NavService);

  getStockDetails(baseParams: BaseParams): Observable<StockDetail[] | null> {
    const { customerId, sourceSystemCodes } = baseParams;
    const stockTypes = ['AtWarehouse', 'InTransit'];

    const stockDetailsParams: StockDetailsParams[] = sourceSystemCodes.reduce(
      (acc: StockDetailsParams[], sourceSystemCode) => {
        if (sourceSystemCode === '5') {
          return [...acc, { sourceSystemId: sourceSystemCode, stockType: stockTypes[0] }];
        }
        return [
          ...acc,
          ...stockTypes.map((stockType) => ({ sourceSystemId: sourceSystemCode, stockType })),
        ];
      },
      [],
    );

    return combineLatest(
      stockDetailsParams.map((params) => {
        const url = `${environment.stockApi}/details/customers/${customerId}`;
        return this.http.get<StockDetail[]>(url, { params: params as any }).pipe(
          startWith(null),
          map((stockDetails) => fixStockDetails(stockDetails, params.sourceSystemId)),
          catchError(() => of([] as StockDetail[])),
        );
      }),
    ).pipe(
      tap((responses) => this.navService.toggleLoadingBar(responses)),
      map(flattenResponses),
    );
  }

  getStockOverview(baseParams: BaseParams): Observable<StockDetail[] | null> {
    return this.getStockDetails(baseParams).pipe(map(this.pivotStockDetails));
  }

  getStockPackageDetails(detailsParams: DetailsParams): Observable<StockPackageDetail[] | null> {
    const { customerId, sourceSystemCode, orderNumber, orderRowNumber, warehouseCode } =
      detailsParams;
    const url = `${environment.stockApi}/packagedetails/customers/${customerId}`;
    const params = {
      sourcesystemid: sourceSystemCode,
      ordernumber: Number(orderNumber),
      orderrownumber: Number(orderRowNumber),
      warehousecode: warehouseCode,
      stocktype: 'AtWarehouse',
    };
    return this.http.get<StockPackageDetail[]>(url, { params }).pipe(
      startWith(null),
      map((stockPackageDetails) => fixStockPackageDetail(stockPackageDetails)),
      catchError(() => of([] as StockPackageDetail[])),
    );
  }

  pivotStockDetails(rows: StockDetail[] | null | undefined): PivotRow[] | null {
    if (!rows) return null;
    const unitPipe = new UnitPipe();

    const totalRows = rows.map((row) => ({
      ...row,
      warehouse: { name: totalWarehouseStock, code: 'Total' },
    }));

    const rowsWithTotal = [...rows, ...totalRows];

    const pivotTable: Record<string, any> = {};

    rowsWithTotal.forEach((row) => {
      const groupValue = [
        row.customerArticleCode + '	',
        row.productVariantName,
        unitPipe.transform(row.stiffness),
        unitPipe.transform(row.grammage),
        unitPipe.transform(row.width),
      ]
        .filter((value) => !!value)
        .join(' ');

      const pivotValue = row.warehouse?.name ?? 'Unknown';
      const value = row.stockQuantity?.weight?.value ?? 0;

      if (!pivotTable[groupValue]) {
        pivotTable[groupValue] = { product: groupValue, [pivotValue]: { value: 0, rows: [] } };
      }

      if (!pivotTable[groupValue][pivotValue]) {
        pivotTable[groupValue][pivotValue] = { value: 0, rows: [] };
      }

      pivotTable[groupValue][pivotValue].value = pivotTable[groupValue][pivotValue].value + value;
      pivotTable[groupValue][pivotValue].rows.push(row);
    });

    return Object.values(pivotTable);
  }
}
